// apps/client/src/pages/transactions/Transactions.tsx
import {
  Account,
  AllocationStatus,
  ResearchActivity,
  ResearchActivityType,
  TransactionWithAllocations,
  UserRole,
  XeroSyncTransactionsResponse,
} from '@avanzu/types';
import { calcProjectStartAndEndDates } from '@avanzu/utils';
import {
  AutoFixHigh,
  PlaylistAdd,
  PlaylistAddTwoTone,
  PlaylistRemove,
  ScienceOutlined,
  Block,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import HelpComponent from '../../components/HelpComponent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { fetchProjectAccountRules, fetchProjectActivities } from '../../services/projects';
import { fetchProjectSalarySummary } from '../../services/salaries';
import { fetchTransactions, saveTransactionAllocations } from '../../services/transactions';
import PersonPercentIcon from './components/PersonPercentIcon';
import TransactionActivityAllocation from './components/TransactionActivityAllocation';
import TransactionQuotaDisplay from './components/TransactionQuotaDisplay';
import XeroSyncTransactions from './components/XeroSyncTransactions';
import { getTransactionQuotaInfo } from '../../utils/quota';
import { classifyXeroSyncError } from '../../utils/xeroSyncError';

export const TransactionPageRoles = [
  UserRole.AVANZU,
  UserRole.OWNER,
  UserRole.ADMIN,
  UserRole.MEMBER,
];

export const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionWithAllocations[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  // True when the project has usable salary data — gates the salary-loading
  // toggle in each transaction row. Computed from the salary summary endpoint.
  const [hasSalaryData, setHasSalaryData] = useState(false);
  // Precomputed salary-loading preview shared by every row. When loading is on
  // for a transaction, the row displays these per-activity percentages directly
  // in the (disabled) inputs so the user sees the actual derived split.
  // Numbers are percentages (0..100). totalPct is the per-row total R&D %.
  const [salaryPreview, setSalaryPreview] = useState<{
    totalPct: number;
    activityPct: Record<string, number>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  // Optimistic UI: rows hidden the moment exclude/apply-rule/save is clicked,
  // while the request is still in flight. Committed (actually removed) on
  // success, restored on failure — so slow connections get instant feedback.
  const [optimisticallyHidden, setOptimisticallyHidden] = useState<Set<string>>(new Set());
  const [groupByTransactions, setGroupByTransactions] = useState(false);
  const [xeroConnectionStatus, setXeroConnectionStatus] = useState<{
    connected: boolean;
    statusMessage?: string;
    hasBeenSetByError?: boolean;
  }>({ connected: true });
  const { organisationId, currentOrganisation } = useAuth();
  const { currentProject, refreshCurrentProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();

  const projectId = currentProject?.id;

  // Initialize Xero connection status from current organisation only once or when not set by error
  useEffect(() => {
    if (currentOrganisation && !xeroConnectionStatus.hasBeenSetByError) {
      setXeroConnectionStatus({
        connected: currentOrganisation?.connectedToXero ? true : false,
        statusMessage: undefined,
        hasBeenSetByError: false,
      });
    }
  }, [currentOrganisation, xeroConnectionStatus.hasBeenSetByError]);

  const loadActivitiesAndAccounts = async (organisationId: string, projectId: string) => {
    const [activitiesData, projectAccountsData, salarySummary] = await Promise.all([
      fetchProjectActivities(organisationId, projectId),
      fetchProjectAccountRules(organisationId, projectId),
      // Best-effort: surface a no-salary-data state instead of throwing if the
      // endpoint errors. The toggle stays disabled in that case.
      fetchProjectSalarySummary(organisationId, projectId).catch(() => null),
    ]);
    setActivities(activitiesData.activities);
    setAccounts(projectAccountsData.accounts);

    // Derive the salary-loading preview from the summary. This mirrors the
    // server-side derivation in `computeProjectSalaryDerivation` but lives on
    // the client so we can render per-activity percentages instantly in the
    // disabled inputs without an extra round-trip per row.
    //   totalPct      = summary.projectAllocation (already 0..100)
    //   activityPct[a] = totalPct * (activityWages[a] / projectWages)
    if (
      salarySummary &&
      salarySummary.projectAllocation > 0 &&
      salarySummary.totalWages > 0 &&
      salarySummary.projectWages > 0
    ) {
      const totalPct = salarySummary.projectAllocation;
      const projectWages = salarySummary.projectWages;
      const activityPct: Record<string, number> = {};

      // Round per-activity to 2dp, then apply the residual to the largest
      // entry so the displayed numbers add up to totalPct (rounded to 2dp).
      const rows = (salarySummary.allocations || []).map((a) => ({
        activityId: a.activityId,
        pct: (a.wages / projectWages) * totalPct,
      }));
      const rounded = rows.map((r) => ({ ...r, pct: Math.round(r.pct * 100) / 100 }));
      if (rounded.length > 0) {
        const sum = rounded.reduce((s, r) => s + r.pct, 0);
        const residual = Math.round((totalPct - sum) * 100) / 100;
        if (residual !== 0) {
          let maxIdx = 0;
          for (let i = 1; i < rounded.length; i++) {
            if (rounded[i].pct > rounded[maxIdx].pct) maxIdx = i;
          }
          rounded[maxIdx].pct = Math.round((rounded[maxIdx].pct + residual) * 100) / 100;
        }
      }
      rounded.forEach((r) => {
        activityPct[r.activityId] = r.pct;
      });

      setHasSalaryData(true);
      setSalaryPreview({
        totalPct: Math.round(totalPct * 100) / 100,
        activityPct,
      });
    } else {
      setHasSalaryData(false);
      setSalaryPreview(null);
    }
  };

  const loadTransactions = async (
    organisationId: string,
    pageArg: number,
    pageSizeArg: number,
    groupBy: boolean
  ) => {
    const { startDate, endDate } = calcProjectStartAndEndDates(
      currentProject!.taxYear.substring(0, 4),
      currentOrganisation!.taxYearStartMonth.toString()
    );

    const response = await fetchTransactions(organisationId, {
      allocationStatus: AllocationStatus.PENDING,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      page: pageArg + 1, // API is 1-indexed; MUI is 0-indexed
      pageSize: pageSizeArg,
      sortBy: groupBy ? 'accountCode' : 'date',
    });

    // Show all returned transactions, including BAS-excluded ones. The
    // R&D Excel export already keys on allocationStatus rather than taxType,
    // so a BAS-excluded row only ends up in the claim if the user explicitly
    // allocates it. Hiding them previously left users with a "N remaining"
    // counter that disagreed with an empty table.
    setTransactions(response.transactions);
    setTotal(response.total);
  };

  const loadData = async (organisationId: string, projectId: string) => {
    try {
      setLoading(true);
      await loadActivitiesAndAccounts(organisationId, projectId);
      await loadTransactions(organisationId, 0, pageSize, groupByTransactions);
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to load data', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reload activities/accounts + first page when project/org changes.
  useEffect(() => {
    if (!projectId || !organisationId) return;
    setPage(0);
    loadData(organisationId, projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, organisationId]);

  // Reload transactions when paging changes (but not on initial mount — loadData handles that).
  useEffect(() => {
    if (!projectId || !organisationId) return;
    if (activities.length === 0) return; // initial load not finished
    loadTransactions(organisationId, page, pageSize, groupByTransactions).catch((err) => {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to load transactions', {
        variant: 'error',
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, groupByTransactions]);

  // Optimistic-hide helpers. Hide fires on click (before the request), commit
  // on success (row leaves the data array), unhide on failure (row returns to
  // exactly where it was, since it never left the array).
  const hideTransaction = (transactionId: string) => {
    setOptimisticallyHidden((prev) => new Set(prev).add(transactionId));
    setTotal((prev) => Math.max(0, prev - 1));
  };
  const unhideTransaction = (transactionId: string) => {
    setOptimisticallyHidden((prev) => {
      const next = new Set(prev);
      next.delete(transactionId);
      return next;
    });
    setTotal((prev) => prev + 1);
  };
  const commitTransaction = (transactionId: string) => {
    setOptimisticallyHidden((prev) => {
      const next = new Set(prev);
      next.delete(transactionId);
      return next;
    });
    setTransactions((prev) => prev.filter((t) => t.transaction.id !== transactionId));
  };

  const handleConfirm = async (
    transactionId: string,
    allocations: Record<string, number>,
    options?: { applySalaryLoading?: boolean }
  ) => {
    if (!projectId || !organisationId) return;
    // Guard against double-fires (e.g. a double-click landing before the
    // first hide has re-rendered) — one request per transaction at a time.
    if (optimisticallyHidden.has(transactionId)) return;

    // Hide the row immediately — on high-latency connections the round trip
    // takes seconds, and an unchanged row reads as "nothing happened".
    hideTransaction(transactionId);
    setSaving(transactionId);

    try {
      if (options?.applySalaryLoading) {
        // Salary-loading mode: ignore the per-activity map and let the server
        // derive the split from the project's salary mix.
        await saveTransactionAllocations(organisationId, projectId, transactionId, [], {
          applySalaryLoading: true,
        });
      } else if (Object.keys(allocations).length === 0) {
        // Empty allocations means exclude from the R&D claim.
        await saveTransactionAllocations(organisationId, projectId, transactionId, []);
      } else {
        const allocationsList = Object.entries(allocations)
          .filter(([_, percentage]) => percentage > 0)
          .map(([activityId, percentage]) => ({
            activityId,
            transactionId,
            allocation: percentage,
            allocatedBy: organisationId,
            notes: null,
          }));

        await saveTransactionAllocations(organisationId, projectId, transactionId, allocationsList);
      }

      commitTransaction(transactionId);

      // Refresh project to update transaction count. The save already
      // succeeded — a failed refresh must not resurrect the row.
      await refreshCurrentProject().catch((err) => {
        console.error('Failed to refresh project after allocation save:', err);
      });
    } catch (err) {
      // Bring the row back so the user can retry.
      unhideTransaction(transactionId);
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save allocations', {
        variant: 'error',
      });
    } finally {
      setSaving(null);
    }
  };

  const handleSyncComplete = async (result: XeroSyncTransactionsResponse) => {
    if (!organisationId || !projectId) return;

    if (result.success) {
      enqueueSnackbar(`Successfully fetched ${result.processed} transactions`, {
        variant: 'success',
      });
      await loadData(organisationId, projectId);

      // Reset connection status on successful sync
      setXeroConnectionStatus({
        connected: true,
        statusMessage: undefined,
        hasBeenSetByError: false,
      });
      return;
    }

    // Partial-failure (typically Xero 429): rows may have landed before the
    // rate-limit hit. Refresh so the UI reflects what's actually in the DB.
    if (classifyXeroSyncError(result.message).isRateLimit) {
      enqueueSnackbar('Xero API rate limit — try again in a minute', { variant: 'warning' });
      await loadData(organisationId, projectId);
    }
  };

  // Calculate date range for current project's tax year.
  //
  // `taxYearStartMonth` is configured per-organisation and stored as a
  // 1-indexed calendar month (1 = January … 7 = July). JS Date's month arg is
  // 0-indexed, so we subtract 1. We also construct via Date.UTC() so the
  // resulting `.toISOString().split('T')[0]` later in the pipeline yields
  // exactly the intended calendar date rather than shifting by the local
  // timezone offset (e.g. AEST midnight → previous UTC day).
  const { startDate, endDate } = useMemo(() => {
    if (!currentProject || !currentOrganisation) {
      return { startDate: undefined, endDate: undefined };
    }

    const year = Number(currentProject.taxYear.substring(0, 4));
    const month = currentOrganisation.taxYearStartMonth; // 1-indexed (7 = July)

    return {
      // First day of the tax-year-start month at UTC midnight.
      startDate: new Date(Date.UTC(year, month - 1, 1)),
      // Last day of the month before the start month in the following year
      // (day=0 of month M = last day of month M-1).
      endDate: new Date(Date.UTC(year + 1, month - 1, 0)),
    };
  }, [currentProject, currentOrganisation]);

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);

  // Server orders by accountCode when groupBy is enabled, so no client sort
  // needed. Optimistically-hidden rows stay in `transactions` (so an error can
  // restore them in place) but are filtered from what's rendered.
  const displayTransactions = useMemo(
    () => transactions.filter((t) => !optimisticallyHidden.has(t.transaction.id)),
    [transactions, optimisticallyHidden]
  );

  // Check quota limits
  const quotaInfo = useMemo(() => {
    if (!currentOrganisation || !currentProject) return null;
    return getTransactionQuotaInfo(currentOrganisation, currentProject.transactionCount);
  }, [currentOrganisation, currentProject]);

  if (!currentOrganisation || loading) {
    return (
      <ContentWrapper>
        <PaperSection title={`Transactions Review  - ${currentProject?.name}`}>
          <LoadingComponent context="Loading transactions..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  if (activities.length === 0) {
    return (
      <ContentWrapper>
        <PaperSection title={`Transactions Review  - ${currentProject?.name}`}>
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No Research Activities"
              message="Create research activities to start allocating transaction costs."
              button={
                <Button
                  component={RouterLink}
                  to={`/app/activities/${projectId}`}
                  variant="contained"
                  size="small"
                >
                  Create Your First Activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  // Don't flip to the empty state while optimistic removals are still in
  // flight — a failure would flash the whole page away and back.
  if (total === 0 && !loading && optimisticallyHidden.size === 0) {
    return (
      <ContentWrapper>
        <PaperSection
          title={`Transactions Review  - ${currentProject?.name}`}
          action={
            <>
              <XeroSyncTransactions
                onSyncComplete={handleSyncComplete}
                startDate={startDate}
                endDate={endDate}
                disabled={!activities || activities.length === 0 || loading}
                variant="contained"
                size="small"
              />
              <TransactionsHelp />
            </>
          }
        >
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<PlaylistAddTwoTone />}
              title="No pending transactions"
              message="There are no pending transactions to be allocated, sync with xero to fetch and import new transactions."
              showBorder={false}
              button={
                (currentProject?.transactionCount ?? 0) > 0 && projectId ? (
                  <Button
                    component={RouterLink}
                    to={`/app/reports/${projectId}`}
                    variant="outlined"
                  >
                    View allocated transactions
                  </Button>
                ) : undefined
              }
            />
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Transactions Review  - ${currentProject?.name}`}
        action={
          <>
            <XeroSyncTransactions
              onSyncComplete={handleSyncComplete}
              startDate={startDate}
              endDate={endDate}
              disabled={!activities || activities.length === 0 || loading}
              variant="contained"
              size="small"
            />
            <TransactionsHelp />
          </>
        }
      >
        {/* Filters */}
        <Box
          sx={{
            mb: 1,
            mt: -2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={groupByTransactions}
                onChange={(e) => {
                  setGroupByTransactions(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label={<Typography variant="body2">Group by account code</Typography>}
          />

          {/* Transaction Quota Display */}
          <TransactionQuotaDisplay />
        </Box>

        <TableContainer
          component={Box}
          sx={{
            mx: -6,
            px: 6,
            width: 'calc(100% + 96px)',
            // maxHeight: '70vh', // this forces vertical scroll in the panel
            minHeight: '60vh',
            //height: '70vh',
            position: 'relative',
            overflowX: 'scroll',
            '&::-webkit-scrollbar': {
              WebkitAppearance: 'none',
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '4px',
              backgroundColor: 'rgba(0, 0, 0, .3)',
            },
          }}
        >
          {/* Quota limit overlay */}
          {quotaInfo?.isAtLimit && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: (theme) => `${theme.palette.background.paper}aa`,
                backdropFilter: 'blur(2px)',
                zIndex: (theme) => theme.zIndex.appBar + 2,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: 8,
              }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  maxWidth: 500,
                  p: 4,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  //  boxShadow: 2,
                }}
              >
                <EmptyStateContent
                  icon={<Block />}
                  title="Transaction Limit Reached"
                  message="You've reached the maximum number of transactions you can assign in this project for your subscription account."
                  button={
                    <Button
                      component={RouterLink}
                      to="/settings/plan"
                      variant="contained"
                      color="primary"
                      size="medium"
                    >
                      Upgrade to add more
                    </Button>
                  }
                  showBorder={false}
                />
              </Box>
            </Box>
          )}

          <Table stickyHeader size="small">
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  zIndex: (theme) => theme.zIndex.appBar + 1,
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  borderTop: '0px none',
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 350, minWidth: 350, fontWeight: 600 }}>
                  Transactions
                  {!loading && total > 0 && <span> ({total} remaining)</span>}
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell sx={{ width: 50, minWidth: 50 }}></TableCell>
                {sortedActivities.map((activity) => (
                  <TableCell
                    key={activity.id}
                    sx={{ width: 100, minWidth: 100, p: 1, verticalAlign: 'top' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                        size="small"
                        color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        align="center"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {activity.name}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <LoadingComponent />
              ) : displayTransactions.length > 0 ? (
                displayTransactions.map((transactionWithAlloc, index) => {
                  // Check if this is the start of a new account code group
                  const isNewGroup =
                    groupByTransactions &&
                    index > 0 &&
                    displayTransactions[index - 1].transaction.accountCode !==
                      transactionWithAlloc.transaction.accountCode;

                  return (
                    <React.Fragment key={transactionWithAlloc.transaction.id}>
                      {/* Add divider row for new account code groups */}
                      {isNewGroup && (
                        <TableRow>
                          <TableCell
                            colSpan={4 + sortedActivities.length}
                            sx={{
                              p: 0,
                              borderBottom: '1px solid',
                              borderColor: 'secondary.main',
                              backgroundColor: 'action.hover',
                            }}
                          />
                        </TableRow>
                      )}
                      <TransactionActivityAllocation
                        transaction={transactionWithAlloc.transaction}
                        activities={sortedActivities}
                        accounts={accounts}
                        onConfirm={handleConfirm}
                        isSaving={saving === transactionWithAlloc.transaction.id}
                        disabled={quotaInfo?.isAtLimit || false}
                        hasSalaryData={hasSalaryData}
                        salaryPreview={salaryPreview}
                      />
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4 + sortedActivities.length}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </PaperSection>
    </ContentWrapper>
  );
};

export default Transactions;

const TransactionsHelp: React.FC = () => (
  <HelpComponent title="Allocate transaction costs to research activities.">
    <Typography variant="body2" paragraph>
      Each transaction must be allocated to either 0% or 100% across activities before it can be
      confirmed. 0% allocation excludes the transaction from R&D claims. Click on the account code
      to create or edit a rule for the account.
    </Typography>
    <Divider sx={{ my: 1 }} />
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoFixHigh sx={{ fontSize: '16px', color: 'success.main' }} />
        <Typography variant="caption">Apply account rule to this transaction</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <PlaylistRemove sx={{ fontSize: '16px', color: 'error.main' }} />
        <Typography variant="caption">Excludes transaction from R&D claim</Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <PlaylistAdd sx={{ fontSize: '16px', color: 'success.main' }} />
        <Typography variant="caption">Allocate transaction to research activities</Typography>
      </Stack>
    </Stack>
    <Divider sx={{ my: 1 }} />
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <PersonPercentIcon sx={{ fontSize: '16px', color: 'inherit' }} />
        <Typography variant="caption">
          Toggles salary allocation to this transaction. The transaction allocation will be
          proportioned based on the percentage of salary allocated to the project. Use for salary
          related transactions like payroll tax or insurance.
        </Typography>
      </Stack>
    </Stack>
  </HelpComponent>
);
